import React from "react";
import { Link } from "react-router-dom";
import { events, leaders } from "../data/mock";
import { newsItems } from "./NewsEvents";
import { undergraduateProgrammes } from "./departments/programmeIndex";
import HeroCarousel from "../components/HeroCarousel";
import { ArrowRight, Calendar, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

const SectionTitle = ({ children, sub }) => (
  <div className="mb-10">
    <h2
      className="text-4xl md:text-5xl text-brand font-semibold"
      style={{ fontFamily: "'Cormorant Garamond', serif" }}
    >
      {children}
    </h2>
    {sub && <p className="text-[#3a3a3a]/80 mt-2 italic">{sub}</p>}
  </div>
);

const coeCards = [
  {
    id: "royal-enfield-training-centre",
    name: "Royal Enfield Training Centre",
    short: "Mechanical Engineering",
    image: "/COE/ROYAL.jpg",
  },
  {
    id: "nvidia-jetson-innovation-learning-centre",
    name: "NVIDIA Jetson Orin Nano Innovation Learning Centre",
    short: "Electronics & Communication",
    image: "/COE/Nvidia.jpg",
  },
  {
    id: "toyota-centre-of-excellence",
    name: "Toyota Centre of Excellence",
    short: "Mechanical Engineering",
    image: "/COE/toyota.png",
  },
  {
    id: "microchip-embedded-systems",
    name: "Microchip Embedded Systems",
    short: "Electrical & Electronics",
    image: "/COE/microchipp.jpg",
  },
  {
    id: "cyber-security-innovation-lab",
    name: "Cyber Security Innovation Lab (LEAP)",
    short: "CSE / CSE (AI & ML)",
    image: "/COE/cyber.jpg",
  },
  // {
  //   id: "leap",
  //   name: "LEAP",
  //   short: "Centre of Excellence",
  //   image: "/COE/leap.jpg",
  // },
];

const Home = () => {
  const undergraduateDepartments = undergraduateProgrammes;
  const [leaderIdx, setLeaderIdx] = useState(0);
  const visibleLeaders = 2;
  const maxIdx = Math.max(0, leaders.length - visibleLeaders);
  const next = () => setLeaderIdx((i) => Math.min(i + 1, maxIdx));
  const prev = () => setLeaderIdx((i) => Math.max(i - 1, 0));

  return (
    <main>
      <HeroCarousel />

      {/* COE */}
      <section className="bg-surface-alt/60 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-6 mb-14">
            <div className="bg-white border border-brand/15 p-6">
              {/* <span className="text-xs tracking-[0.2em] text-brand font-sans-ui font-semibold">
                VISION
              </span> */}
              <h2
                className="text-3xl text-brand font-semibold mt-2"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Vision
              </h2>
              <p className="text-[#2a2a2a]/85 leading-relaxed mt-3">
                To nurture talent and enrich society through excellence in technical education, research, and innovation.
              </p>
            </div>

            <div className="bg-white border border-brand/15 p-6">
              {/* <span className="text-xs tracking-[0.2em] text-brand font-sans-ui font-semibold">
                MISSION
              </span> */}
              <h2
                className="text-3xl text-brand font-semibold mt-2"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Mission
              </h2>
              <ul className="space-y-2 mt-3">
                {[
                  "To promote innovative pedagogy, foster interdisciplinary learning, and enhance conceptual understanding.",
                  "To build professional competence, inculcate ethical values, and develop entrepreneurial thinking.",
                  "To strengthen industry-institute partnerships and explore global collaborations.",
                  "To inculcate a culture of socially responsible citizenship.",
                  "To focus on holistic and sustainable development.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-[#2a2a2a]/85 leading-relaxed">
                    <CheckCircle2 size={16} className="text-brand flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <SectionTitle sub="Industry-linked learning spaces and training platforms">
            Center of Excellence
          </SectionTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coeCards.map((centre) => (
              <Link
                to={`/coe/${centre.id}`}
                key={centre.id}
                className="relative group block aspect-[4/3] overflow-hidden"
              >
                <img
                  src={centre.image}
                  alt={centre.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-dark/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="text-surface/85 text-xs tracking-widest font-sans-ui">
                    {centre.short}
                  </div>
                  <h3 className="text-surface text-xl font-semibold mt-1 leading-snug">
                    {centre.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Our Recruiters */}
      <section className="py-14 w-full overflow-hidden px-[30px]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 mb-8">
          <SectionTitle sub="Companies that hire our graduates">
            Our Recruiters
          </SectionTitle>
        </div>
        <div className="marquee-track flex items-center gap-20">
          {[...Array(43)].concat([...Array(43)]).map((_, i) => (
            <img
              key={i}
              src={`/recruters/${(i % 43) + 1}.png`}
              alt={`Recruiter ${(i % 43) + 1}`}
              className="max-h-24 w-auto object-contain flex-shrink-0"
            />
          ))}
        </div>
      </section>

      {/* Study/Departments */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTitle sub="Seven UG Programs. Hundreds of opportunities.">
            Study at VDIT
          </SectionTitle>
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <h3 className="text-2xl text-brand font-semibold mb-3">
                Undergraduate Programmes
              </h3>
              <p className="text-[#3a3a3a]/85 leading-relaxed">
                VDIT offers eight 4-year B.E. degree programmes across emerging
                and core disciplines, including the new{" "}
                <Link to="/academics#cse-aids" className="text-brand underline">
                  B.E. in CSE (Data Science)
                </Link>
                . Each curriculum is aligned with VTU and enriched through
                industry electives, mini-projects and capstone experiences.
              </p>
            </div>
            <div>
              <h3 className="text-2xl text-brand font-semibold mb-3">
                Postgraduate Programmes
              </h3>
              <p className="text-[#3a3a3a]/85 leading-relaxed">
                Our postgraduate offerings include the{" "}
                <span className="text-brand">M.Tech in Industrial Electronics</span>{" "}
                and <span className="text-brand">M.Tech in Thermal Power Engineering</span>.
                These two-year programmes provide depth in research methodology
                and are designed for professionals and graduates aiming to lead
                technical innovation.
              </p>
            </div>
            <div>
              <h3 className="text-2xl text-brand font-semibold mb-3">
                Research &amp; Doctoral
              </h3>
              <p className="text-[#3a3a3a]/85 leading-relaxed">
                Faculty across VDIT actively engage in research in renewable
                energy, IoT, embedded systems, sustainable construction, and
                applied AI. Multiple departments are recognised VTU research
                centres.
              </p>
            </div>
            <div>
              <h3 className="text-2xl text-brand font-semibold mb-3">
                Continuing Education
              </h3>
              <p className="text-[#3a3a3a]/85 leading-relaxed">
                Through skill development cells and the entrepreneurship
                development centre, VDIT runs short-term certificate courses for
                practitioners and rural learners across North Karnataka.
              </p>
            </div>
          </div>
          <div className="mt-12 text-center">
            <Link
              to="/academics"
              className="inline-flex items-center gap-2 bg-brand text-surface px-7 py-3 text-sm font-sans-ui font-semibold tracking-wider hover:bg-brand-dark transition"
            >
              Know More <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Departments grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <SectionTitle sub="Explore our seven engineering departments">
          Departments
        </SectionTitle>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {undergraduateDepartments.map((d) => (
            <Link
              to={`/academics#${d.id}`}
              key={d.id}
              className="relative group block aspect-[4/3] overflow-hidden"
            >
              <img
                src={d.image}
                alt={d.name}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-dark/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="text-surface/85 text-xs tracking-widest font-sans-ui">
                  {d.short}
                </div>
                <h3 className="text-surface text-xl font-semibold mt-1 leading-snug">
                  {d.name}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Community / Leaders carousel */}
      {/* <section className="bg-surface-alt/60 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTitle sub="Meet the people steering VDIT">
            VDIT Community
          </SectionTitle>
          <div className="relative">
            <button
              onClick={prev}
              disabled={leaderIdx === 0}
              className="absolute -left-2 md:-left-6 top-1/2 -translate-y-1/2 z-10 bg-brand text-surface rounded-full p-2 disabled:opacity-30 hover:bg-brand-dark"
              aria-label="Previous"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={next}
              disabled={leaderIdx === maxIdx}
              className="absolute -right-2 md:-right-6 top-1/2 -translate-y-1/2 z-10 bg-brand text-surface rounded-full p-2 disabled:opacity-30 hover:bg-brand-dark"
              aria-label="Next"
            >
              <ChevronRight size={22} />
            </button>
            <div className="grid md:grid-cols-2 gap-8">
              {leaders
                .slice(leaderIdx, leaderIdx + visibleLeaders)
                .map((l) => (
                  <div
                    key={l.id}
                    className="bg-white p-6 md:p-8 flex flex-col md:flex-row gap-6 border border-brand/10"
                  >
                    <div className="w-32 h-32 md:w-40 md:h-44 flex-shrink-0 overflow-hidden">
                      <img
                        src={l.image}
                        alt={l.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-[#3a3a3a]/85 text-sm leading-relaxed">
                        {l.bio}
                      </p>
                      <div className="mt-4">
                        <h3 className="text-brand text-xl font-semibold">
                          {l.name}
                        </h3>
                        <p className="text-[#3a3a3a] text-sm italic">
                          {l.role}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: maxIdx + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setLeaderIdx(i)}
                  className={`w-3 h-3 rounded-full border border-brand ${
                    i === leaderIdx ? "bg-brand" : "bg-transparent"
                  }`}
                  aria-label={`Page ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* Explore VDIT */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <SectionTitle>Explore VDIT</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <ExploreTile
            label="Our Campus"
            to="/campus"
            image="/HomePage/explore/campus.jpg"
          />
          <ExploreTile
            label="Admissions"
            to="/admissions"
            image="/HomePage/explore/admision.jpg"
          />
          <ExploreTile
            label="Placements"
            to="/placements"
            image="/HomePage/explore/placement.jpg"
          />
          <ExploreTile
            label="Alumni"
            to="/alumni"
            image="/HomePage/explore/2.jpg"
          />
        </div>
      </section>

      {/* News */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <SectionTitle>News</SectionTitle>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {newsItems.slice(0, 6).map((item) => (
            <Link
              to="/news-events"
              key={item.id}
              className="bg-white card-hover overflow-hidden border border-brand/10 block"
            >
              <div className="aspect-[16/10] overflow-hidden bg-white">
                <img
                  src={item.image}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-5">
                <h3
                  className="text-brand text-xl font-semibold leading-snug"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {item.title}
                </h3>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/news-events"
            className="inline-flex items-center gap-2 bg-brand text-surface px-7 py-3 text-sm font-sans-ui font-semibold tracking-wider hover:bg-brand-dark transition"
          >
            More News <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Events */}
      {/* <section className="bg-surface-alt/60 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTitle>Events</SectionTitle>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((ev) => (
              <Link
                to={`/event/${ev.id}`}
                key={ev.id}
                className="border-l-4 border-brand bg-white p-5 card-hover block"
              >
                <div className="flex items-center gap-2 text-brand text-xs font-sans-ui tracking-wider mb-2">
                  <Calendar size={14} />
                  {ev.date} · {ev.time}
                </div>
                <h3 className="text-[#2a2a2a] text-lg font-semibold leading-snug">
                  {ev.title}
                </h3>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/news-events"
              className="inline-flex items-center gap-2 bg-brand text-surface px-7 py-3 text-sm font-sans-ui font-semibold tracking-wider hover:bg-brand-dark transition"
            >
              More Events <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section> */}
    </main>
  );
};

const ExploreTile = ({ label, to, image }) => (
  <Link to={to} className="group block aspect-square relative overflow-hidden">
    <img
      src={image}
      alt={label}
      loading="lazy"
      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
    />
    <div className="absolute inset-0 bg-brand-dark/40 group-hover:bg-brand-dark/60 transition" />
    <div className="absolute inset-0 flex items-center justify-center">
      <span
        className="text-surface text-xl md:text-2xl font-semibold tracking-wide flex items-center gap-2"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {label} <ArrowRight size={18} />
      </span>
    </div>
  </Link>
);

export default Home;
